"use client"

import { useEffect, useState } from "react"
import axios from "axios"
import { motion } from "framer-motion"
import { AlertTriangle, CheckCircle, Save, RefreshCw, Trash2 } from "lucide-react"
import "../styles/AvisosAdmin.css"

const AvisosAdmin = () => {
  const [eventos, setEventos] = useState([])
  const [usuariosDono, setUsuariosDono] = useState([])
  const [eventoSelecionado, setEventoSelecionado] = useState("")
  const [usuarioSelecionado, setUsuarioSelecionado] = useState("")
  const [publico, setPublico] = useState("comprador")
  const [mensagem, setMensagem] = useState("")
  const [avisoAtual, setAvisoAtual] = useState(null)
  const [loading, setLoading] = useState(false)
  const [statusMessage, setStatusMessage] = useState({ type: "", message: "" })
  const [showConfirmModal, setShowConfirmModal] = useState(false)

  useEffect(() => {
    carregarEventos()
    carregarUsuariosDono()
  }, [])

  useEffect(() => {
    carregarAvisoExistente()
  }, [publico, eventoSelecionado, usuarioSelecionado])

  const carregarEventos = async () => {
    try {
      setLoading(true)
      const res = await axios.get(`${process.env.REACT_APP_API_URL}/events`)
      setEventos(res.data)
    } catch (error) {
      console.error("Erro ao carregar eventos:", error)
      setStatusMessage({
        type: "error",
        message: "Erro ao carregar eventos. Tente novamente.",
      })
    } finally {
      setLoading(false)
    }
  }

  const carregarUsuariosDono = async () => {
    try {
      setLoading(true)
      const token = localStorage.getItem("token")
      const res = await axios.get(`${process.env.REACT_APP_API_URL}/admin/donos`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      setUsuariosDono(res.data)
    } catch (error) {
      console.error("Erro ao buscar donos:", error)
      setStatusMessage({
        type: "error",
        message: "Erro ao carregar usuários. Tente novamente.",
      })
    } finally {
      setLoading(false)
    }
  }

  const carregarAvisoExistente = async () => {
    try {
      setLoading(true)
      if (publico === "comprador" && eventoSelecionado) {
        const res = await axios.get(`${process.env.REACT_APP_API_URL}/avisos/${eventoSelecionado}/comprador`)
        if (res.data?._id) {
          setAvisoAtual(res.data)
          setMensagem(res.data.mensagem)
        } else {
          setAvisoAtual(null)
          setMensagem("")
        }
      }

      if (publico === "dono" && usuarioSelecionado) {
        const res = await axios.get(`${process.env.REACT_APP_API_URL}/avisos/${usuarioSelecionado}/dono`)
        if (res.data?._id) {
          setAvisoAtual(res.data)
          setMensagem(res.data.mensagem)
        } else {
          setAvisoAtual(null)
          setMensagem("")
        }
      }
    } catch (error) {
      console.error("Erro ao buscar aviso:", error)
    } finally {
      setLoading(false)
    }
  }

  const salvarAviso = async () => {
    try {
      setLoading(true)
      setStatusMessage({ type: "", message: "" })

      if (!mensagem.trim()) {
        setStatusMessage({
          type: "error",
          message: "Por favor, digite uma mensagem para o aviso.",
        })
        setLoading(false)
        return
      }

      if (publico === "comprador" && !eventoSelecionado) {
        setStatusMessage({
          type: "error",
          message: "Por favor, selecione um evento.",
        })
        setLoading(false)
        return
      }

      if (publico === "dono" && !usuarioSelecionado) {
        setStatusMessage({
          type: "error",
          message: "Por favor, selecione um usuário.",
        })
        setLoading(false)
        return
      }

      const payload = {
        tipo: publico,
        mensagem,
        ativo: true,
        ...(publico === "comprador" ? { eventoId: eventoSelecionado } : { usuarioId: usuarioSelecionado }),
      }

      if (avisoAtual) {
        await axios.put(`${process.env.REACT_APP_API_URL}/avisos/${avisoAtual._id}`, payload)
        setStatusMessage({
          type: "success",
          message: "Aviso atualizado com sucesso!",
        })
      } else {
        await axios.post(`${process.env.REACT_APP_API_URL}/avisos`, payload)
        setStatusMessage({
          type: "success",
          message: "Aviso criado com sucesso!",
        })
      }
    } catch (error) {
      console.error("Erro ao salvar aviso:", error)
      setStatusMessage({
        type: "error",
        message: "Erro ao salvar aviso. Tente novamente.",
      })
    } finally {
      setLoading(false)
    }
  }

  const excluirAviso = async () => {
    if (!avisoAtual) return

    setShowConfirmModal(true) // Mostra o modal de confirmação em vez de usar confirm()
  }

  const confirmarExclusao = async () => {
    try {
      setLoading(true)
      await axios.delete(`${process.env.REACT_APP_API_URL}/avisos/${avisoAtual._id}`)
      setAvisoAtual(null)
      setMensagem("")
      setStatusMessage({
        type: "success",
        message: "Aviso excluído com sucesso!",
      })
    } catch (error) {
      console.error("Erro ao excluir aviso:", error)
      setStatusMessage({
        type: "error",
        message: "Erro ao excluir aviso. Tente novamente.",
      })
    } finally {
      setLoading(false)
      setShowConfirmModal(false) // Fecha o modal após a operação
    }
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className="aviso-admin-container"
    >
      <h2>Avisos do Sistema</h2>

      {statusMessage.message && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className={`status-message ${statusMessage.type === "success" ? "status-success" : "status-error"}`}
        >
          {statusMessage.type === "success" ? <CheckCircle size={18} /> : <AlertTriangle size={18} />}
          {statusMessage.message}
        </motion.div>
      )}

      <div className="form-group">
        <label>Público:</label>
        <select
          value={publico}
          onChange={(e) => {
            setPublico(e.target.value)
            setMensagem("")
            setAvisoAtual(null)
            setStatusMessage({ type: "", message: "" })
          }}
        >
          <option value="comprador">Comprador</option>
          <option value="dono">Dono</option>
        </select>
      </div>

      {publico === "comprador" && (
        <div className="form-group">
          <label>Evento:</label>
          <select value={eventoSelecionado} onChange={(e) => setEventoSelecionado(e.target.value)}>
            <option value="">Selecione um evento</option>
            {eventos.map((evento) => (
              <option key={evento._id} value={evento._id}>
                {evento.nome}
              </option>
            ))}
          </select>
        </div>
      )}

      {publico === "dono" && (
        <div className="form-group">
          <label>Dono do Evento:</label>
          <select value={usuarioSelecionado} onChange={(e) => setUsuarioSelecionado(e.target.value)}>
            <option value="">Selecione um usuário</option>
            {usuariosDono.map((user) => (
              <option key={user._id} value={user._id}>
                {user.nome} ({user.email})
              </option>
            ))}
          </select>
        </div>
      )}

      <div className="form-group">
        <label>Mensagem do Aviso:</label>
        <textarea
          rows="5"
          value={mensagem}
          onChange={(e) => setMensagem(e.target.value)}
          placeholder="Digite a mensagem do aviso..."
        />
      </div>

      <div className="form-actions">
        {avisoAtual && (
          <motion.button
            whileHover={{ scale: 1.03 }}
            whileTap={{ scale: 0.97 }}
            className="btn-excluir"
            onClick={excluirAviso}
            disabled={loading}
          >
            <Trash2 size={18} />
            Excluir Aviso
          </motion.button>
        )}

        <motion.button
          whileHover={{ scale: 1.03 }}
          whileTap={{ scale: 0.97 }}
          className="btn-salvar55"
          onClick={salvarAviso}
          disabled={loading}
        >
          {loading ? (
            <>
              <RefreshCw size={18} className="icon-spin" />
              Processando...
            </>
          ) : (
            <>
              <Save size={18} />
              {avisoAtual ? "Atualizar Aviso" : "Salvar Aviso"}
            </>
          )}
        </motion.button>
      </div>
      {showConfirmModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h3>Confirmar Exclusão</h3>
            <p>Tem certeza que deseja excluir este aviso?</p>
            <div className="modal-actions">
              <button className="btn-cancelar" onClick={() => setShowConfirmModal(false)}>
                Cancelar
              </button>
              <button className="btn-confirmar" onClick={confirmarExclusao}>
                Confirmar
              </button>
            </div>
          </div>
        </div>
      )}
    </motion.div>
  )
}

export default AvisosAdmin
